import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EditIcon,
} from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { FaHospitalUser, FaPhone } from 'react-icons/fa6';
import { downloadResourceMatrixFile } from '../../services/resource-matrix-search/downloadResMatrixFile';
import { updateResourceMatrix } from '../../services/resource-matrix-search/updateResMatrix';
import { SearchResults } from '../../types';
import { SubCard } from './SubCardNotes';
interface Props {
  searchResults: SearchResults;
  setSearchResults: React.Dispatch<
    React.SetStateAction<SearchResults | null>
  >;
}

interface HandleCellClickFunction {
  (
    row: string,
    column: string,
    value: string | null,
    uuid: string | null,
    expirationDate?: string | null
  ): void;
}
export function SearchResultsCardView({
  searchResults,
  setSearchResults,
}: Props) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editValue, setEditValue] = useState<string | null>('');
  const [editRow, setEditRow] = useState<string>('');
  const [editColumn, setEditColumn] = useState<string>('');
  const [uuid, setUuid] = useState<string | null>('');
  const [expirationDate, setExpirationDate] = useState('');
  const [dataUpdating, setDataUpdating] = useState(false);

  // Pagination variables
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  // Logic to handle pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  //Downloading vairables
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadKey, setDownloadKey] = useState('');

  const currentItems = useMemo(() => {
    const items = Object.keys(searchResults['phone/fax']).slice(
      indexOfFirstItem,
      indexOfLastItem
    );
    return items;
  }, [searchResults, indexOfFirstItem, indexOfLastItem]);

  const excludedColumns = [
    'UUID',
    'AlertExpiration',
    'NotesExpiration',
    'office notes:',
    'daily alert',
    'sheet',
    'phone',
    'URL',
    'name',
    'file',
    'practice',
    'provider name (athena user id)',
    'phone/fax',
    'office name',
  ];

  const handleCellClick: HandleCellClickFunction = useCallback(
    (
      row: string,
      column: string,
      value: string | null,
      uuid: string | null,
      expirationDate?: string | null
    ) => {
      setEditRow(row);
      setEditColumn(column);
      setEditValue(value);
      setExpirationDate(
        expirationDate ? expirationDate.toString() : ''
      );
      setUuid(uuid ? uuid.toString() : '');
      onOpen();
    },
    [onOpen]
  );

  const handleCellChange = useCallback(async () => {
    setDataUpdating(true);
    const updatedResults = { ...searchResults };
    updatedResults[editColumn][editRow] = editValue;

    try {
      if (editColumn && editValue && uuid) {
        let data;

        if (editColumn === 'office notes:') {
          // Update office notes and notes expiration separately
          const officeNotesUpdatePromise = updateResourceMatrix(
            'office notes:',
            uuid,
            editValue
          );
          const expirationUpdatePromise = updateResourceMatrix(
            'NotesExpiration',
            uuid,
            expirationDate
          );

          const [officeNotesUpdate, expirationUpdate] =
            await Promise.all([
              officeNotesUpdatePromise,
              expirationUpdatePromise,
            ]);
          setDataUpdating(
            officeNotesUpdate.loading && expirationUpdate.loading
          );

          if (
            officeNotesUpdate.data === 'Update Successful' &&
            expirationUpdate.data === 'Update Successful'
          ) {
            toast({
              title: 'Office Notes Updated',
              description:
                'Office notes and expiration updated successfully.',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            updatedResults['NotesExpiration'][editRow] =
              expirationDate;

            setSearchResults(updatedResults);
            onClose();
            setEditRow('');
            setEditColumn('');
            setEditValue('');
            setExpirationDate('');
            setUuid('');
          } else {
            setDataUpdating(false);

            throw new Error(
              'Failed to update office notes or expiration.'
            );
          }
        } else if (editColumn === 'daily alert') {
          const dailyAlertsUpdatePromise = await updateResourceMatrix(
            'daily alert',
            uuid,
            editValue
          );
          const alertExpirationUpdatePromise =
            await updateResourceMatrix(
              'AlertExpiration',
              uuid,
              expirationDate
            );
          const [dailyAlertsUpdate, alertExpirationUpdate] =
            await Promise.all([
              dailyAlertsUpdatePromise,
              alertExpirationUpdatePromise,
            ]);
          setDataUpdating(
            dailyAlertsUpdate.loading && alertExpirationUpdate.loading
          );

          if (
            dailyAlertsUpdate.data === 'Update Successful' &&
            alertExpirationUpdate.data === 'Update Successful'
          ) {
            toast({
              title: 'Daily Alerts Updated',
              description:
                'Daily alerts and expiration updated successfully.',
              status: 'success',
              duration: 5000,
              isClosable: true,
            });
            updatedResults['AlertExpiration'][editRow] =
              expirationDate;
            setSearchResults(updatedResults);
            onClose();
            setEditRow('');
            setEditColumn('');
            setEditValue('');
            setExpirationDate('');
            setUuid('');
          } else {
            setDataUpdating(false);

            throw new Error(
              'Failed to update daily alerts or expiration.'
            );
          }
        } else {
          // Update other columns separately
          const response = await updateResourceMatrix(
            editColumn,
            uuid,
            editValue
          );
          data = response.data;
          setDataUpdating(response.loading);
        }

        if (!data || data === 'Update Successful') {
          toast({
            title: 'Update Successful',
            description: 'Resource matrix updated successfully.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          setSearchResults(updatedResults);
          onClose();
          setEditRow('');
          setEditColumn('');
          setEditValue('');
          setExpirationDate('');
          setUuid('');
        } else {
          setDataUpdating(false);

          throw new Error('Failed to update resource matrix.');
        }
      }
    } catch (error) {
      setDataUpdating(false);

      toast({
        title: 'Update Failed',
        description: 'Failed to update resource matrix.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [
    searchResults,
    editColumn,
    editRow,
    editValue,
    uuid,
    expirationDate,
    toast,
    setSearchResults,
    onClose,
  ]);

  const handleDownload = async (filename: string, key: string) => {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    setDownloadKey(key);
    try {
      const { url, loading } = await downloadResourceMatrixFile(
        filename
      );

      if (!loading && url) {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.target = '_blank'; // Open in a new tab
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        toast({
          title: 'Download Started',
          description: `Downloading ${filename} in a new tab.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      } else {
        console.error('Failed to get file URL');
        toast({
          title: 'Download Failed',
          description: 'Failed to get the file URL.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to download file:', error);
      toast({
        title: 'Download Failed',
        description: 'An error occurred while downloading the file.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDownloading(false);
      setDownloadKey('');
    }
  };

  const hasResults = Object.keys(searchResults).length > 0;

  return (
    <Box>
      {hasResults ? (
        <>
          <SimpleGrid
            columns={{ sm: 1, md: 2, lg: 3 }}
            spacing={6}
            templateColumns="repeat(auto-fit, minmax(320px, 1fr))"
          >
            {currentItems.map((key) => {
              const fileName = searchResults['file'][key];
              const uuid = searchResults['UUID'][key];
              const providerName =
                searchResults['provider name (athena user id)'][key];
              const phoneFax = searchResults['phone/fax'][key];
              const npi = searchResults['npi'][key];
              const officeName = searchResults['office name'][key];
              const dailyAlertValue =
                searchResults['daily alert'][key];
              const alertExpirationValue =
                searchResults['AlertExpiration'][key];
              const officeNotesValue =
                searchResults['office notes:'][key];
              const notesExpirationValue =
                searchResults['NotesExpiration'][key];
              return (
                <Card
                  key={key}
                  bg="white"
                  shadow="lg"
                  borderWidth="1px"
                  borderRadius="lg"
                  overflow="hidden"
                  position="relative"
                  variant={'outline'}
                >
                  <CardHeader>
                    <Flex
                      direction="row"
                      justify="space-between"
                      alignItems="flex-start"
                      flexWrap="wrap"
                      gap="0.75rem"
                    >
                      <Flex
                        direction={'column'}
                        align="flex-start"
                        flex="1 1 16rem"
                        minWidth="12rem"
                        maxWidth="100%"
                      >
                        <Heading
                          size="md"
                          fontWeight={'bold'}
                          color="text.primary"
                          whiteSpace="normal"
                          wordBreak="break-word"
                        >
                          {providerName?.toString()}
                        </Heading>
                        {npi && (
                          <Tooltip
                            label={`National Provider Identifier`}
                            fontSize={'sm'}
                          >
                            <Badge colorScheme="teal" mt={2}>
                              {npi.toString()}
                            </Badge>
                          </Tooltip>
                        )}
                      </Flex>
                      {fileName && (
                        <Tooltip
                          label={`Download ${fileName}`}
                          fontSize={'sm'}
                        >
                          <Button
                            onClick={() =>
                              handleDownload(fileName, key)
                            }
                            isLoading={
                              isDownloading && key === downloadKey
                            }
                            isDisabled={isDownloading}
                          >
                            <DownloadIcon cursor="pointer" />
                          </Button>
                        </Tooltip>
                      )}
                    </Flex>
                  </CardHeader>
                  <CardBody>
                    <Flex flexWrap="wrap" gap="0.75rem">
                      {officeName && (
                        <Flex
                          ml={2}
                          alignItems={'center'}
                          flex="1 1 12rem"
                          minWidth="12rem"
                          gap="0.35rem"
                        >
                          <Icon
                            as={FaHospitalUser}
                            w={5}
                            h={5}
                            color={'blue.600'}
                            mr={1}
                          />
                          <Text fontWeight={'semibold'} color={'blue.600'}>
                            Office
                          </Text>
                          <Text
                            fontSize={'md'}
                            color="gray.700"
                            whiteSpace="normal"
                            wordBreak="break-word"
                          >
                            : {officeName?.toString()}
                          </Text>
                        </Flex>
                      )}
                      {phoneFax && (
                        <Flex
                          ml={2}
                          alignItems={'center'}
                          flex="1 1 12rem"
                          minWidth="12rem"
                          gap="0.35rem"
                        >
                          <Icon
                            as={FaPhone}
                            w={4}
                            h={4}
                            color={'blue.600'}
                            mr={1}
                          />
                          <Text
                            fontWeight={'semibold'}
                            color={'blue.600'}
                          >
                            Phone / Fax
                          </Text>
                          <Text
                            fontSize={'md'}
                            color="gray.700"
                            whiteSpace="normal"
                            wordBreak="break-word"
                          >
                            : {phoneFax?.toString()}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                    <SimpleGrid columns={2} mt={3} spacing={5}>
                      <SubCard
                        bodyText={dailyAlertValue?.toString()}
                        expiration={alertExpirationValue?.toString()}
                        type={'daily alert'}
                        handleCellClick={handleCellClick}
                        keyValue={key.toString()}
                        uuid={uuid}
                      />
                      <SubCard
                        bodyText={officeNotesValue?.toString()}
                        expiration={notesExpirationValue?.toString()}
                        handleCellClick={handleCellClick}
                        type={'office notes:'}
                        keyValue={key.toString()}
                        uuid={uuid}
                      />
                    </SimpleGrid>
                  </CardBody>

                  <Accordion allowToggle>
                    <AccordionItem>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          Additional Information
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <List spacing={3}>
                          {Object.keys(searchResults).map(
                            (columnName) => {
                              if (
                                !excludedColumns.includes(columnName)
                              ) {
                                const value =
                                  searchResults[columnName][key];
                                if (value) {
                                  return (
                                    <ListItem
                                      key={`${key}-${columnName}`}
                                      display="flex"
                                      alignItems="center"
                                      minWidth="80"
                                      _hover={{
                                        background: 'gray.100',
                                      }}
                                    >
                                      <Text fontWeight="bold" mr={2}>
                                        {columnName}:
                                      </Text>
                                      <Box flex="1">
                                        <Text
                                          isTruncated
                                          cursor="pointer"
                                        >
                                          {value}
                                        </Text>
                                      </Box>
                                      {!excludedColumns.includes(
                                        columnName
                                      ) && (
                                        <Button
                                          variant={'ghost'}
                                          onClick={() =>
                                            handleCellClick(
                                              key,
                                              columnName,
                                              value,
                                              uuid
                                            )
                                          }
                                        >
                                          <EditIcon
                                            w={4}
                                            h={4}
                                            color="blue.500"
                                          />
                                        </Button>
                                      )}
                                    </ListItem>
                                  );
                                }
                              }
                              return null;
                            }
                          )}
                        </List>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                </Card>
              );
            })}
          </SimpleGrid>
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Edit Value for {editColumn}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {editColumn === 'office notes:' && (
                  <>
                    <Input
                      value={editValue?.toString()}
                      onChange={(e) => setEditValue(e.target.value)}
                      marginBottom={4}
                      placeholder="Office Notes Value"
                    />
                    <Input
                      value={expirationDate}
                      onChange={(e) =>
                        setExpirationDate(e.target.value)
                      }
                      type="date"
                      placeholder="Notes Expiration"
                    />
                  </>
                )}
                {editColumn === 'daily alert' && (
                  <>
                    <Input
                      value={editValue?.toString()}
                      onChange={(e) => setEditValue(e.target.value)}
                      marginBottom={4}
                      placeholder="Daily Alerts Value"
                    />
                    <Input
                      value={expirationDate}
                      onChange={(e) =>
                        setExpirationDate(e.target.value)
                      }
                      type="date"
                      placeholder="Alert Expiration"
                    />
                  </>
                )}
                {!(
                  editColumn === 'office notes:' ||
                  editColumn === 'daily alert'
                ) && (
                  <Input
                    value={editValue?.toString()}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder={`${editColumn} value`}
                  />
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="blue"
                  mr={3}
                  onClick={() => handleCellChange()}
                  isLoading={dataUpdating}
                  loadingText={'Updating...'}
                >
                  Update
                </Button>
                <Button onClick={onClose}>Cancel</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <HStack spacing={4} justify="flex-start" margin={6}>
            <Text>
              Showing {indexOfFirstItem + 1} to{' '}
              {Math.min(
                indexOfLastItem,
                Object.keys(searchResults['phone/fax']).length
              )}{' '}
              of {Object.keys(searchResults['phone/fax']).length}{' '}
              entries
            </Text>
            <IconButton
              icon={<ChevronLeftIcon />}
              aria-label="Previous"
              onClick={() => setCurrentPage(currentPage - 1)}
              isDisabled={currentPage === 1}
            />
            <Text>{`Page ${currentPage}`}</Text>
            <IconButton
              icon={<ChevronRightIcon />}
              aria-label="Next"
              onClick={() => setCurrentPage(currentPage + 1)}
              isDisabled={
                indexOfLastItem >=
                Object.keys(searchResults['phone/fax']).length
              }
            />
          </HStack>
        </>
      ) : (
        <Box p={4} textAlign="center">
          <strong>No search results found.</strong>
        </Box>
      )}
    </Box>
  );
}
